import { useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { authApi } from "../../lib/auth";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

type RequestPasswordResetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultEmail?: string;
  lockEmail?: boolean;
};

const RequestPasswordResetModal = ({
  isOpen,
  onClose,
  defaultEmail = "",
  lockEmail = false,
}: RequestPasswordResetModalProps) => {
  const [email, setEmail] = useState(defaultEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setEmail(defaultEmail);
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [defaultEmail, isOpen]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await authApi.csrf();
      await authApi.requestPasswordReset({ email: email.trim() });
      setSuccessMessage("If an account exists for that email, a password reset link has been sent.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        setErrorMessage("Enter a valid email address.");
      } else {
        setErrorMessage("Unable to send a reset link right now. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) {
          onClose();
        }
      }}
      title="Forgot your password?"
      panelClassName="max-w-lg"
    >
      <div className="space-y-4">
        <p className="text-sm leading-6 text-myteengig-text-muted">
          Enter the account email and we&apos;ll send a secure link to choose a new password.
        </p>

        {successMessage ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {successMessage}
          </div>
        ) : (
          <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
            {lockEmail ? (
              <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 px-4 py-3">
                <p className="text-sm font-medium text-myteengig-text-primary">Reset link destination</p>
                <p className="mt-1 break-all text-sm text-myteengig-text-muted">
                  This reset link will be sent to <span className="font-semibold text-myteengig-text-primary">{email}</span>.
                </p>
              </div>
            ) : (
              <div>
                <label htmlFor="password-reset-email" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                  Email address
                </label>
                <input
                  id="password-reset-email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={320}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="you@example.com"
                />
              </div>
            )}

            {errorMessage ? <p className="text-sm font-medium text-red-600">{errorMessage}</p> : null}

            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !email.trim()}>
                {isSubmitting ? "Sending..." : "Send reset link"}
              </Button>
            </div>
          </form>
        )}

        {successMessage ? (
          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default RequestPasswordResetModal;
